/* global BigInt */

import { useEffect, useRef, useState } from 'react';
import { useSolanaLive } from '../hooks/useSolanaLive';
import { useTradeActions } from '../hooks/useTradeActions';
import { useTradeQuote } from '../hooks/useTradeQuote';
import { useUserBalances } from '../hooks/useUserBalances';
import { useUserPosition } from '../hooks/useUserPosition';
import { convertSolToLamports } from '../solana/utils/tradeUnits';
import { formatTokenBalance } from '../solana/utils/tokenBalanceFormat';
import { LiveRoundCurveChart } from '../components/LiveRoundCurveChart';
import UserPositionPanel from '../components/UserPositionPanel';
import { recordSuccessfulBuy, recordSuccessfulSell } from '../services/positionService';
import { getBuyQuote } from '../solana/quotes/buyQuote';
import { getSellQuote } from '../solana/quotes/sellQuote';

const PRESET_SOL_AMOUNTS = ['0.001', '0.01', '0.1', '1'];
const SELL_PERCENTAGE_OPTIONS = [25, 50, 75, 100];

function getDumpLamports(dumpVault) {
  const usableLamports = dumpVault?.usableLamports ?? dumpVault?.data?.usable_lamports;

  if (usableLamports !== null && usableLamports !== undefined && usableLamports !== '') {
    return usableLamports;
  }

  return dumpVault?.lamports ?? null;
}

function getDumpProgressPercent(dumpLamports, dumpTriggerLamports) {
  const currentAmount = Number(dumpLamports);
  const targetAmount = Number(dumpTriggerLamports);

  if (!Number.isFinite(currentAmount) || !Number.isFinite(targetAmount) || targetAmount <= 0) {
    return null;
  }

  return Math.max(0, Math.min((currentAmount / targetAmount) * 100, 100));
}

function getDumpProgressIntensity(progressPercent) {
  if (progressPercent === null) {
    return 'low';
  }

  if (progressPercent >= 90) {
    return 'near-full';
  }

  if (progressPercent >= 70) {
    return 'high';
  }

  if (progressPercent >= 35) {
    return 'medium';
  }

  return 'low';
}

function formatValue(value, suffix = '', fallback = 'Pending game state') {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return `${value}${suffix}`;
}

function capitalizeFirstLetter(str) {
  if (!str || typeof str !== 'string') {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatCountdown(endTimeEpoch, countdownEndEpoch) {
  if (!endTimeEpoch) {
    return {
      value: 'N/A',
      isRestartCountdown: false,
    };
  }

  const endTimeMs = Number(endTimeEpoch) * 1000;

  if (!Number.isFinite(endTimeMs) || endTimeMs <= 0) {
    return {
      value: 'N/A',
      isRestartCountdown: false,
    };
  }

  const now = Date.now();

  if (now >= endTimeMs) {
    const countdownEndMs = Number(countdownEndEpoch) * 1000;
    const restartCountdownEndMs =
      Number.isFinite(countdownEndMs) && countdownEndMs > endTimeMs ? countdownEndMs : endTimeMs + 10000;

    if (now >= restartCountdownEndMs) {
      return {
        value: '0',
        isRestartCountdown: true,
      };
    }

    const remainingRestartSeconds = Math.max(Math.ceil((restartCountdownEndMs - now) / 1000), 0);

    return {
      value: String(remainingRestartSeconds),
      isRestartCountdown: true,
    };
  }

  const remainingMs = Math.max(endTimeMs - now, 0);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const seconds = String(totalSeconds % 60).padStart(2, '0');

  return {
    value: `${hours}:${minutes}:${seconds}`,
    isRestartCountdown: false,
  };
}

function formatTokenSupply(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  const normalizedValue = String(value);

  if (!/^\d+$/.test(normalizedValue)) {
    return 'N/A';
  }

  const wholePart = normalizedValue.length > 6 ? normalizedValue.slice(0, -6) : '0';
  const fractionalPart = normalizedValue.slice(-6).padStart(6, '0');
  const trimmedFractionalPart = fractionalPart.replace(/0+$/, '');
  const formattedWholePart = Number(wholePart).toLocaleString();

  if (!trimmedFractionalPart) {
    return formattedWholePart;
  }

  return `${formattedWholePart}.${trimmedFractionalPart}`;
}

function formatLamportsToSol(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  const normalizedValue = String(value);

  if (!/^\d+$/.test(normalizedValue)) {
    return 'N/A';
  }

  const wholePart = normalizedValue.length > 9 ? normalizedValue.slice(0, -9) : '0';
  const fractionalPart = normalizedValue.slice(-9).padStart(9, '0');
  const trimmedFractionalPart = fractionalPart.replace(/0+$/, '');
  const formattedWholePart = Number(wholePart).toLocaleString();

  if (!trimmedFractionalPart) {
    return `${formattedWholePart} SOL`;
  }

  return `${formattedWholePart}.${trimmedFractionalPart} SOL`;
}

function normalizeLamportsValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalizedValue = String(value).trim();

  if (!/^\d+$/.test(normalizedValue)) {
    return null;
  }

  return window.BigInt(normalizedValue);
}

function formatLamportsToSolInput(value) {
  const normalizedValue = normalizeLamportsValue(value);

  if (normalizedValue === null) {
    return '';
  }

  const valueString = normalizedValue.toString();
  const wholePart = valueString.length > 9 ? valueString.slice(0, -9) : '0';
  const fractionalPart = valueString.slice(-9).padStart(9, '0');
  const trimmedFractionalPart = fractionalPart.replace(/0+$/, '');

  if (!trimmedFractionalPart) {
    return wholePart;
  }

  return `${wholePart}.${trimmedFractionalPart}`;
}

function getNextPresetAmount(currentAmount, presetAmount, maxBuyLamports) {
  const incrementLamports = convertSolToLamports(presetAmount);
  const normalizedCurrentAmount = String(currentAmount ?? '').trim();
  const maxLamports = normalizeLamportsValue(maxBuyLamports);
  let currentLamports = 0n;

  if (normalizedCurrentAmount) {
    try {
      currentLamports = convertSolToLamports(normalizedCurrentAmount);
    } catch (error) {
      currentLamports = 0n;
    }
  }

  const nextLamports = currentLamports + incrementLamports;
  const cappedLamports = maxLamports !== null && nextLamports > maxLamports ? maxLamports : nextLamports;

  return formatLamportsToSolInput(cappedLamports);
}

function convertDisplayBalanceToAtomic(displayBalance, decimals) {
  const normalizedBalance = String(displayBalance ?? '')
    .trim()
    .replace(/,/g, '');
  const normalizedDecimals = Number.parseInt(decimals ?? 0, 10);

  if (!normalizedBalance || !/^\d+(\.\d+)?$/.test(normalizedBalance)) {
    return 0n;
  }

  if (!Number.isFinite(normalizedDecimals) || normalizedDecimals < 0) {
    return 0n;
  }

  const [wholePart, fractionalPart = ''] = normalizedBalance.split('.');
  let scale = 1n;

  for (let index = 0; index < normalizedDecimals; index += 1) {
    scale *= 10n;
  }

  const safeFractionalPart = fractionalPart.slice(0, normalizedDecimals).padEnd(normalizedDecimals, '0');

  return window.BigInt(wholePart || '0') * scale + window.BigInt(safeFractionalPart || '0');
}

function getSellDisplayAmount(displayBalance, decimals, percentage) {
  const balanceAtomic = convertDisplayBalanceToAtomic(displayBalance, decimals);

  if (balanceAtomic <= 0n || !percentage) {
    return '0';
  }

  const selectedAtomic = (balanceAtomic * window.BigInt(percentage)) / 100n;

  if (selectedAtomic <= 0n) {
    return '0';
  }

  return formatTokenBalance(selectedAtomic.toString(), decimals);
}

function getOwnershipRewardLamports(currentReserveLamports, ownedTokenAmountAtomic, currentSupplyAtomic) {
  const reserveLamports = normalizeLamportsValue(currentReserveLamports);
  const ownedTokens = normalizeLamportsValue(ownedTokenAmountAtomic);
  const totalSupply = normalizeLamportsValue(currentSupplyAtomic);

  if (reserveLamports === null || ownedTokens === null || totalSupply === null || totalSupply <= 0n) {
    return null;
  }

  if (reserveLamports <= 0n || ownedTokens <= 0n) {
    return 0n;
  }

  return (reserveLamports * ownedTokens) / totalSupply;
}

function formatOwnershipPercent(ownedTokenAmountAtomic, currentSupplyAtomic) {
  const ownedTokens = normalizeLamportsValue(ownedTokenAmountAtomic);
  const totalSupply = normalizeLamportsValue(currentSupplyAtomic);

  if (ownedTokens !== null && ownedTokens <= 0n) {
    return '0%';
  }

  if (ownedTokens === null || totalSupply === null || totalSupply <= 0n) {
    return 'N/A';
  }

  const percentBasisPoints = (ownedTokens * 10000n) / totalSupply;

  return `${(Number(percentBasisPoints) / 100).toFixed(2)}%`;
}

function Home({ connectionState, phantomInstalled, walletConnected, walletPublicKey }) {
  const { currentGameState, currentGamePubkey, currentReserveVault, dumpVault, globalConfig } = useSolanaLive();
  const { buyCurrentGame, isBuying, sellCurrentGame, isSelling } = useTradeActions();
  const {
    error: balanceError,
    loading: balancesLoading,
    refreshBalances,
    roundTokenAta,
    roundTokenBalance,
    roundTokenAmountAtomic,
    solBalance,
  } = useUserBalances(walletPublicKey);
  const {
    position,
    loading: positionLoading,
    error: positionError,
    currentValue,
    unrealizedPnL,
    unrealizedPnLPercent,
    refreshPosition,
  } = useUserPosition(walletPublicKey, roundTokenAmountAtomic);
  const [tradeSide, setTradeSide] = useState('buy');
  const [buyAmount, setBuyAmount] = useState('');
  const [selectedSellPercentage, setSelectedSellPercentage] = useState(null);
  const [selectedSellAmount, setSelectedSellAmount] = useState('0');
  const [orderFeedback, setOrderFeedback] = useState('idle');
  const [countdown, setCountdown] = useState(
    formatCountdown(currentGameState?.end_ts, currentGameState?.countdown_end_ts)
  );
  const [dumpSparkActive, setDumpSparkActive] = useState(false);
  const previousDumpProgressRef = useRef(null);
  const previousDumpedLamportsRef = useRef(null);
  const [dollarSigns, setDollarSigns] = useState([]);
  const dollarSignIdRef = useRef(0);
  const tradeDisabled = !walletConnected;
  const activeRound = globalConfig?.current_game_id || '';
  
  // Determine display status based on game state and countdown
  const rawStatus = currentGameState?.status_label || currentGameState?.status;
  const currentStatus = capitalizeFirstLetter(
    countdown.isRestartCountdown 
      ? 'New Game Starting' 
      : rawStatus === 'final_countdown' || rawStatus === 'redeeming' 
        ? 'active' 
        : rawStatus
  );
  
  const currentTokenSupply = formatTokenSupply(currentGameState?.current_supply);
  const currentReserveSol = formatLamportsToSol(currentReserveVault?.lamports);
  const dumpLamports = getDumpLamports(dumpVault);
  const dumpTriggerLamports = globalConfig?.dump_trigger_lamports ?? null;
  const dumpThresholdSol = formatLamportsToSol(dumpTriggerLamports);
  const dumpProgressPercent = getDumpProgressPercent(dumpLamports, dumpTriggerLamports);
  const dumpProgressWidth = dumpProgressPercent ?? 0;
  const dumpProgressIntensity = getDumpProgressIntensity(dumpProgressPercent);
  const maxBuyLamports = globalConfig?.max_buy_lamports ?? null;
  const currentRoundDecimals = currentGameState?.decimals ?? 0;
  const currentRoundBalanceAtomic = convertDisplayBalanceToAtomic(roundTokenBalance, currentRoundDecimals);
  const quoteInputAmount = tradeSide === 'buy' ? buyAmount : selectedSellAmount;
  const { quoteError, quoteLabel, quoteValue } = useTradeQuote(tradeSide, quoteInputAmount);
  const ownershipPercent = formatOwnershipPercent(
    roundTokenAmountAtomic,
    currentGameState?.current_supply
  );
  const holdingRewardLamports = getOwnershipRewardLamports(
    currentReserveVault?.lamports,
    roundTokenAmountAtomic,
    currentGameState?.current_supply
  );
  const holdingRewardValue = tradeDisabled
    ? 'Connect wallet to view reward'
    : balancesLoading
      ? 'Loading...'
      : balanceError
        ? 'Unable to load reward'
        : currentRoundBalanceAtomic <= 0n
          ? 'Buy to Gain Ownership'
        : holdingRewardLamports === null
          ? 'Waiting for live data'
          : formatLamportsToSol(holdingRewardLamports.toString());
  const hasSellBalance = currentRoundBalanceAtomic > 0n;
  const buyActionDisabled = tradeDisabled || tradeSide !== 'buy' || isBuying;
  const sellActionDisabled =
    tradeDisabled ||
    tradeSide !== 'sell' ||
    isSelling ||
    balancesLoading ||
    Boolean(balanceError) ||
    !hasSellBalance ||
    !selectedSellPercentage ||
    selectedSellAmount === '0';
  const tradeBalanceLabel = tradeSide === 'buy' ? 'Wallet SOL Balance' : 'Wallet Round Token Balance';
  const tradeBalanceValue = tradeDisabled
    ? 'Connect wallet to view balance'
    : balancesLoading
      ? 'Loading...'
      : balanceError
        ? 'Unable to load balance'
        : tradeSide === 'buy'
          ? `${solBalance} SOL`
          : roundTokenBalance;

  useEffect(() => {
    setCountdown(formatCountdown(currentGameState?.end_ts, currentGameState?.countdown_end_ts));

    if (!currentGameState?.end_ts) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setCountdown(formatCountdown(currentGameState?.end_ts, currentGameState?.countdown_end_ts));
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [currentGameState?.end_ts, currentGameState?.countdown_end_ts]);

  useEffect(() => {
    if (dumpProgressPercent === null) {
      previousDumpProgressRef.current = null;
      setDumpSparkActive(false);
      return undefined;
    }

    const previousDumpProgress = previousDumpProgressRef.current;
    previousDumpProgressRef.current = dumpProgressPercent;

    if (previousDumpProgress === null || dumpProgressPercent <= previousDumpProgress) {
      return undefined;
    }

    setDumpSparkActive(true);

    const timerId = window.setTimeout(() => {
      setDumpSparkActive(false);
    }, 900);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [dumpProgressPercent]);

  // Track liquidity dumps and spawn dollar signs
  useEffect(() => {
    const currentDumpedLamports = Number(currentGameState?.total_dumped_lamports || 0);
    
    if (previousDumpedLamportsRef.current === null) {
      previousDumpedLamportsRef.current = currentDumpedLamports;
      return;
    }
    
    const delta = currentDumpedLamports - previousDumpedLamportsRef.current;
    
    // Only spawn dollar signs if there was a meaningful increase (>0.01 SOL)
    if (delta > 10_000_000) {
      // Spawn 3-5 dollar signs
      const count = Math.floor(Math.random() * 3) + 3;
      const newSigns = [];
      
      for (let i = 0; i < count; i++) {
        const id = dollarSignIdRef.current++;
        // Random horizontal position across the dump progress bar
        const leftPercent = 20 + Math.random() * 60;
        // Slight delay between each sign
        const delay = i * 80;
        
        newSigns.push({
          id,
          left: `${leftPercent}%`,
          delay,
        });
        
        // Remove after animation completes (1200ms + delay)
        setTimeout(() => {
          setDollarSigns((prev) => prev.filter((s) => s.id !== id));
        }, 1200 + delay);
      }
      
      setDollarSigns((prev) => [...prev, ...newSigns]);
    }
    
    previousDumpedLamportsRef.current = currentDumpedLamports;
  }, [currentGameState?.total_dumped_lamports]);

  const handleTradeSideChange = (side) => {
    setTradeSide(side);
    setOrderFeedback('idle');

    if (side !== 'sell') {
      setSelectedSellPercentage(null);
      setSelectedSellAmount('0');
    }
  };

  const handleBuyAmountChange = (event) => {
    setBuyAmount(event.target.value);
    setOrderFeedback('idle');
  };

  const handlePresetAmountClick = (presetAmount) => {
    setBuyAmount((currentAmount) => getNextPresetAmount(currentAmount, presetAmount, maxBuyLamports));
    setOrderFeedback('idle');
  };

  const handleSellPercentageClick = (percentage) => {
    setSelectedSellPercentage(percentage);
    setSelectedSellAmount(getSellDisplayAmount(roundTokenBalance, currentRoundDecimals, percentage));
    setOrderFeedback('idle');
  };

  const handleClearAmount = () => {
    if (tradeSide === 'buy') {
      setBuyAmount('');
    } else {
      setSelectedSellPercentage(null);
      setSelectedSellAmount('0');
    }

    setOrderFeedback('idle');
  };

  const handlePlaceOrder = async () => {
    setOrderFeedback('idle');

    try {
      // Capture token balance BEFORE buy (for accurate tracking)
      const tokenBalanceBefore = window.BigInt(roundTokenAmountAtomic || '0');
      let txSignature;

      if (tradeSide === 'buy') {
        if (buyActionDisabled) {
          return;
        }

        // Execute buy transaction (waits for confirmation)
        txSignature = await buyCurrentGame(buyAmount);
      } else {
        if (sellActionDisabled) {
          return;
        }

        // Execute sell transaction
        txSignature = await sellCurrentGame(selectedSellAmount, roundTokenAta);

        // After successful sell, record position
        if (walletPublicKey && currentGameState && globalConfig) {
          try {
            // Get sell quote to extract trade details
            const sellQuote = getSellQuote({
              globalConfig,
              currentGameState,
              tokenAmount: selectedSellAmount,
            });

            // Record sell in database
            await recordSuccessfulSell({
              walletAddress: walletPublicKey,
              gameId: currentGameState.game_id,
              gamePda: currentGamePubkey,
              mint: currentGameState.mint,
              txSignature,
              tokenAmountAtomic: sellQuote.tokenAmountInAtomic,
              solReceivedLamports: sellQuote.netLamportsOut,
            });

            // Refresh position from database
            await refreshPosition();
          } catch (positionError) {
            console.error('Failed to record sell position:', positionError);
            // Don't fail the whole transaction if position recording fails
          }
        }

        setSelectedSellPercentage(null);
        setSelectedSellAmount('0');
      }

      // Refresh balances and get updated values
      const updatedBalances = await refreshBalances();

      // After buy, record position with actual tokens received
      if (tradeSide === 'buy' && walletPublicKey && currentGameState && globalConfig && updatedBalances) {
        try {
          // Calculate ACTUAL tokens received from wallet balance change
          const tokenBalanceAfter = window.BigInt(updatedBalances.roundTokenAmountAtomic || '0');
          const actualTokensReceived = tokenBalanceAfter - tokenBalanceBefore;

          // Get buy quote for SOL spent (still estimated but less critical)
          const buyQuote = getBuyQuote({
            globalConfig,
            currentGameState,
            solAmount: buyAmount,
          });

          // Record buy in database with ACTUAL token amount
          await recordSuccessfulBuy({
            walletAddress: walletPublicKey,
            gameId: currentGameState.game_id,
            gamePda: currentGamePubkey,
            mint: currentGameState.mint,
            txSignature,
            tokenAmountAtomic: actualTokensReceived.toString(),
            solSpentLamports: buyQuote.reserveLamportsIn,
          });

          // Refresh position from database
          await refreshPosition();
        } catch (positionError) {
          console.error('❌ Failed to record buy position:', positionError);
          // Don't fail the whole transaction if position recording fails
        }
      }

      setOrderFeedback('success');
    } catch (error) {
      setOrderFeedback('error');
    }
  };

  return (
    <section className="page-section home-desk">
      <header className="round-bar panel" aria-label="Active round information">
        <div className="round-bar__grid">
          <article className="round-metric">
            <span className="round-metric__label">Active Round</span>
            <strong className="round-metric__value">{formatValue(activeRound, '', 'N/A')}</strong>
          </article>

          <article className="round-metric">
            <span className="round-metric__label">Status</span>
            <strong className="round-metric__value round-metric__value--status">
              <span className="round-bar__signal" aria-hidden="true" />
              {formatValue(currentStatus, '', 'N/A')}
            </strong>
          </article>

          <article className="round-metric">
            <span className="round-metric__label">Countdown Clock</span>
            <strong
              className={
                countdown.isRestartCountdown
                  ? 'round-metric__value round-metric__value--mono round-metric__value--restart'
                  : 'round-metric__value round-metric__value--mono'
              }
            >
              {countdown.value}
            </strong>
          </article>

          <article className="round-metric">
            <span className="round-metric__label">Current Token Supply</span>
            <strong className="round-metric__value">{currentTokenSupply}</strong>
          </article>

          <article className="round-metric">
            <span className="round-metric__label">Game Reserve</span>
            <strong className="round-metric__value">{currentReserveSol}</strong>
          </article>
        </div>
      </header>

      <div className="home-desk__grid">
        <div className="home-desk__main">
          <article className="chart-shell panel" aria-label="Main chart area">
            <div className="chart-shell__frame">
              {/* Dollar sign rain animation */}
              {dollarSigns.map((sign) => (
                <div
                  key={sign.id}
                  className="dollar-rain"
                  style={{
                    left: sign.left,
                    animationDelay: `${sign.delay}ms`,
                  }}
                >
                  $
                </div>
              ))}
              
              <UserPositionPanel
                walletConnected={walletConnected}
                position={position}
                loading={positionLoading}
                error={positionError}
                currentValue={currentValue}
                unrealizedPnL={unrealizedPnL}
                unrealizedPnLPercent={unrealizedPnLPercent}
              />
              
              <div
                className={`chart-shell__topbar dump-progress dump-progress--compact dump-progress--${dumpProgressIntensity}${
                  dumpSparkActive ? ' dump-progress--spark' : ''
                }`}
                aria-label="Liquidity dump progress"
              >
                <span className="chart-shell__topbarLabel">Liqudity Dump Progress</span>
                <div
                  className="dump-progress__track"
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={dumpProgressWidth}
                >
                  <div className="dump-progress__fill" style={{ width: `${dumpProgressWidth}%` }} />
                  <div className="dump-progress__glow" style={{ width: `${dumpProgressWidth}%` }} />
                  <div className="dump-progress__spark" style={{ width: `${dumpProgressWidth}%` }} />
                </div>
                <div className="dump-progress__labels dump-progress__labels--compact">
                  <span>0 SOL</span>
                  <span className="dump-progress__labelsValue">{dumpProgressPercent === null ? 'N/A' : `${dumpProgressPercent.toFixed(1)}%`}</span>
                  <span>{dumpThresholdSol}</span>
                </div>
              </div>
              <div className="chart-shell__body">
                <LiveRoundCurveChart
                  currentGameState={currentGameState}
                  currentReserveVault={currentReserveVault}
                  dumpVault={dumpVault}
                  globalConfig={globalConfig}
                />
              </div>
            </div>
          </article>

          <article className="chat-shell panel">
            <div className="chat-shell__header">
              <p className="eyebrow">Player Chat</p>
            </div>
            <div className="chat-shell__body">
              <div className="chat-shell__placeholder">Chat area</div>
            </div>
            <div className="chat-shell__inputRow">
              <input type="text" placeholder="Type a message..." aria-label="Player chat input" />
              <button className="chat-shell__send" type="button" aria-label="Send message">
                Send
              </button>
            </div>
          </article>
        </div>

        <aside className="home-desk__side">
          <article className="trade-card panel">
              <div className="trade-card__holding">
                <span className="label">Your Reward For Holding</span>
                <strong>{holdingRewardValue}</strong>
                <span className="trade-card__holdingMeta">{ownershipPercent} ownership this round</span>
              </div>

            <div className="trade-card__toggle" role="tablist" aria-label="Trade side selection">
              <button
                className={tradeSide === 'buy' ? 'trade-card__tab is-buy-active' : 'trade-card__tab'}
                type="button"
                onClick={() => handleTradeSideChange('buy')}
              >
                Buy
              </button>
              <button
                className={tradeSide === 'sell' ? 'trade-card__tab is-sell-active' : 'trade-card__tab'}
                type="button"
                onClick={() => handleTradeSideChange('sell')}
              >
                Sell
              </button>
            </div>

            <div className="trade-card__fields">
              <div className="trade-card__balance">
                <div className="trade-card__balance-content">
                  <span className="label">{tradeBalanceLabel}</span>
                  <strong>{tradeBalanceValue}</strong>
                </div>
                <button
                  type="button"
                  className="trade-card__balance-refresh"
                  onClick={refreshBalances}
                  disabled={!walletConnected || balancesLoading}
                  aria-label="Refresh balance"
                  title="Refresh balance"
                >
                  ↻
                </button>
              </div>

              <label className="field" htmlFor="amount">
                <span className="label">Amount</span>
                <div className="field__inputWrap">
                  <input
                    id="amount"
                    name="amount"
                    type="text"
                    value={tradeSide === 'buy' ? buyAmount : selectedSellAmount}
                    placeholder={tradeDisabled ? 'Connect wallet to enter amount' : 'Enter amount'}
                    onChange={tradeSide === 'buy' ? handleBuyAmountChange : undefined}
                    disabled={tradeDisabled || tradeSide === 'sell'}
                  />
                  <button
                    type="button"
                    className="field__clear"
                    onClick={handleClearAmount}
                    disabled={tradeSide === 'buy' ? !buyAmount : !selectedSellPercentage}
                    aria-label="Clear amount"
                  >
                    Clear
                  </button>
                </div>
              </label>

              <div className="trade-card__presets" aria-label="Quick amount buttons">
                {(tradeSide === 'buy' ? PRESET_SOL_AMOUNTS : SELL_PERCENTAGE_OPTIONS).map((presetOption) => (
                  <button
                    key={presetOption}
                    className={
                      tradeSide === 'sell' && selectedSellPercentage === presetOption
                        ? 'trade-card__preset is-active'
                        : 'trade-card__preset'
                    }
                    type="button"
                    onClick={() =>
                      tradeSide === 'buy'
                        ? handlePresetAmountClick(presetOption)
                        : handleSellPercentageClick(presetOption)
                    }
                    disabled={
                      tradeDisabled ||
                      (tradeSide === 'sell' && (!hasSellBalance || balancesLoading || Boolean(balanceError)))
                    }
                  >
                    {tradeSide === 'buy' ? presetOption : `${presetOption}%`}
                  </button>
                ))}
              </div>

              <label className="field" htmlFor="quote">
                <span className="label">{quoteLabel}</span>
                <input
                  id="quote"
                  name="quote"
                  type="text"
                  value={quoteValue}
                  placeholder={quoteError || quoteLabel}
                  readOnly
                  disabled={false}
                />
              </label>

              <button
                className={tradeSide === 'buy' ? 'trade-card__action trade-card__action--buy' : 'trade-card__action trade-card__action--sell'}
                type="button"
                onClick={handlePlaceOrder}
                disabled={tradeSide === 'buy' ? buyActionDisabled : sellActionDisabled}
              >
                {tradeDisabled
                  ? 'Connect Wallet to Trade'
                  : tradeSide === 'buy'
                    ? isBuying
                      ? 'Submitting Buy...'
                      : 'Place Order'
                    : isSelling
                      ? 'Submitting Sell...'
                      : 'Place Order'}
              </button>

              <div
                className={
                  orderFeedback === 'success'
                    ? 'trade-card__status trade-card__status--success'
                    : orderFeedback === 'error'
                      ? 'trade-card__status trade-card__status--error'
                      : 'trade-card__status'
                }
                aria-live="polite"
              >
                {orderFeedback === 'success' ? 'Success' : orderFeedback === 'error' ? 'Error' : ''}
              </div>
            </div>
          </article>
        </aside>
      </div>
    </section>
  );
}

export default Home;